import { useForm } from "antd/es/form/Form";
import PolicySettingsPage, {
  DiffRow,
} from "pages/settings/policy/PolicySettingsPage";
import {
  SettingsSection,
  ToggleRow,
  optionBlockStyle,
} from "pages/settings/settingsRows";
import { translate } from "shared/languages";
import { ADMIN_RESTRICTION_LABELS } from "shared/utils";
import {
  ADMIN_RESTRICTION_FIELD,
  AdminCapability,
  AdminRestrictionField,
  CAPABILITY_ORDER,
} from "shared/utils/capabilities";

// Pengaturan -> Akses Admin -> Pembatasan Admin (Manager-only route).
//
// One switch per capability a Manager can take away from plain Admins.
// Switch ON = restricted, the way Notifikasi's ON = notification active: the
// page is named for restrictions, so "on" means the restriction is in force.
// The page description under the heading says so once for every switch.

type Values = Record<AdminRestrictionField, boolean>;

const GROUPS: { labelKey: string; caps: AdminCapability[] }[] = [
  {
    labelKey: "admin_restrictions.group_packages",
    caps: [AdminCapability.PackageVoidExpiry, AdminCapability.CreditAdjustment],
  },
  {
    labelKey: "admin_restrictions.group_catalog_settings",
    caps: [
      AdminCapability.CatalogPricing,
      AdminCapability.BookingPolicy,
      AdminCapability.PromoReward,
      AdminCapability.OperationalSettings,
    ],
  },
  {
    labelKey: "admin_restrictions.group_sensitive",
    caps: [
      AdminCapability.FinancialAnalytics,
      // The two credential-view rows sit directly above the regenerate row they
      // supersede, so the relationship reads top to bottom with no
      // cross-referencing. Client before instructor, matching every other
      // paired surface in the product.
      AdminCapability.LoginCredentialsClient,
      AdminCapability.LoginCredentialsInstructor,
      AdminCapability.LoginLinkRegenerate,
      AdminCapability.BookingRefund,
    ],
  },
];

// A status (the switch state), so a fixed English word: also the before /
// after text in the change-log diff (PolicySettingsPage).
export const fmtRestricted = (restricted: boolean) =>
  ADMIN_RESTRICTION_LABELS[restricted ? "restricted" : "open"];

const AdminRestrictionsPage = () => {
  const [form] = useForm<Values>();
  return (
    <PolicySettingsPage<Values>
      sectioned
      title={translate("admin_restrictions.title")}
      subtitle={translate("admin_restrictions.subtitle")}
      confirmNote={translate("admin_restrictions.confirm_note")}
      form={form}
      initialValues={(s) =>
        Object.fromEntries(
          CAPABILITY_ORDER.map((cap) => {
            const field = ADMIN_RESTRICTION_FIELD[cap];
            return [field, s[field]];
          }),
        ) as Values
      }
      buildSave={(v, s) => {
        const rows: DiffRow[] = [];
        // Restriction keys only, ever: the server refuses a body that mixes
        // them with policy keys from anyone but a Manager, and a Manager's
        // page has no policy keys to send anyway.
        const payload: Partial<Values> = {};
        CAPABILITY_ORDER.forEach((cap) => {
          const field = ADMIN_RESTRICTION_FIELD[cap];
          const next = !!v[field];
          if (next !== s[field]) {
            rows.push({
              label: translate(`admin_restrictions.${cap}`),
              before: fmtRestricted(s[field]),
              after: fmtRestricted(next),
            });
            payload[field] = next;
          }
        });
        return { rows, payload };
      }}
    >
      {GROUPS.map((group) => (
        <SettingsSection key={group.labelKey} title={translate(group.labelKey)}>
          {group.caps.map((cap, idx) => {
            const field = ADMIN_RESTRICTION_FIELD[cap];
            return (
              <div key={cap} style={optionBlockStyle(idx === group.caps.length - 1)}>
                <ToggleRow
                  name={field}
                  labelKey={`admin_restrictions.${cap}`}
                  descKey={`admin_restrictions.${cap}_desc`}
                />
              </div>
            );
          })}
        </SettingsSection>
      ))}
    </PolicySettingsPage>
  );
};

export default AdminRestrictionsPage;
